import {
  AbortVideoRequestModel,
  IImportVideoApiService,
  ImportProgressEventData,
  InitImportFilesRequestModel,
  InitImportVideoItem,
} from "tir-pm-file-upload-manager";
import { Events, ResponseModel } from "../models";
import ApiService from "@/shared/api";
import WsResponseModelT from "@/shared/api/File/dataSource/api/schema-builder/server/models/WsResponseModelT";
import OnCompleteAddFileResponse from "@/shared/api/File/dataSource/api/project-manager/files/i-services/models/response/OnCompleteAddFileResponse";
import OnFileProgressResponse from "@/shared/api/File/dataSource/api/project-manager/files/i-services/models/response/OnFileProgressResponse";
import OnInitFilesCompleteResponse from "@/shared/api/File/dataSource/api/project-manager/files/i-services/models/response/OnInitFilesCompleteResponse";
import ImportFilesFromUrlRequest from "@/shared/api/File/dataSource/api/project-manager/files/i-services/models/request/ImportFilesFromUrlRequest";
import ImportFilesFromUrlItem from "@/shared/api/File/dataSource/api/project-manager/files/i-services/models/request/ImportFilesFromUrlItem";

export class ImportVideoApiService implements IImportVideoApiService {
  private readonly api = new ApiService();
  /**Событие прогресса импорта файла */
  ProgressEvent = new Events<ImportProgressEventData>();
  /**Событие при успешном импорте файла */
  CompleteEvent = new Events<number>();

  constructor() {
    this.subscribeEvents();
  }

  private subscribeEvents = () => {
    this.api.File.FileService.OnCompleteAddFileEvent.on(this.onCompleteAddFile);
    this.api.File.FileService.OnFileProgressEvent.on(this.onFileProgress);
  };

  /**
   * Обработчик события `FileService.OnCompleteAddFileEvent`
   * @param event Данные события
   */
  private onCompleteAddFile = (
    event: WsResponseModelT<OnCompleteAddFileResponse>
  ) => {
    if (event.IsSuccess && event.Value?.FileId) {
      this.CompleteEvent.trigger(event.Value.FileId);
    }
  };
  /**
   * Обработчик события `FileService.OnFileProgressEvent`
   * @param event Данные события
   */
  private onFileProgress = (
    event: WsResponseModelT<OnFileProgressResponse>
  ) => {
    if (event.IsSuccess && event.Value) {
      this.ProgressEvent.trigger({
        FileId: event.Value.FileId,
        Url: event.Value.OriginalUrl,
        ProgressDownload: event.Value.ProgressDownload,
      });
    }
  };

  /**
   * Ожидает инициализации всех файлов, соответствующих переданным URL.
   * Возвращает карту URL → fileId, когда все файлы будут готовы.
   * Если инициализация завершается с ошибкой или происходит таймаут — возвращается null.
   */
  private waitInitImportFiles(
    urls: Array<string>,
    timeoutMs = 55000
  ): Promise<Array<InitImportVideoItem>> {
    return new Promise<Array<InitImportVideoItem>>((resolve) => {
      const result = new Array<InitImportVideoItem>();

      const checkCompletion = () => {
        const allFilled = urls.length === result.length;

        if (allFilled) {
          cleanup();
          resolve(result);
        }
      };

      /**
       * Обработчик события OnInitFilesComplete.
       * Обновляет карту result, если пришли Id'шники.
       * Если всё успешно — проверяет заполненность.
       * В случае ошибки — завершает с null.
       */
      const onInitFilesComplete = (
        event: WsResponseModelT<OnInitFilesCompleteResponse>
      ) => {
        if (event.IsSuccess) {
          event.Value.Files.forEach((file) => {
            if (urls.includes(file.OriginalUrl)) {
              result.push({
                FileId: file.Id,
                VideoId: file.UploadId,
                Url: file.Url,
                SourceUrL: file.OriginalUrl,
              });
            }
          });
          checkCompletion();
        } else {
          cleanup();
          resolve(null);
        }
      };

      /**
       * Удаляет обработчик события и очищает таймер.
       */
      const cleanup = () => {
        // clearTimeout(timeoutId);
        this.api.File.FileService.OnInitFilesCompleteEvent.removeListener(
          onInitFilesComplete
        );
      };

      // Таймаут на случай, если событие так и не произойдёт
      // const timeoutId = setTimeout(() => {
      //   cleanup();
      //   resolve(null);
      // }, timeoutMs);

      // Подписываемся на событие завершения инициализации
      this.api.File.FileService.OnInitFilesCompleteEvent.on(
        onInitFilesComplete
      );
    });
  }

  private mapInitImportFilesFromUrlRequest = (
    request: InitImportFilesRequestModel
  ) => {
    return new ImportFilesFromUrlRequest({
      FolderId: request.FolderId,
      TaskId: request.TaskId,
      QueueTaskId: request.QueueTaskId,
      ChatId: request.ChatId,
      Files: request.Files.map(
        (file) =>
          new ImportFilesFromUrlItem({
            Url: file.FileUrl,
            Name: file.FileName,
          })
      ),
    });
  };

  /**
   * Инициализация импорта файлов
   * @param request
   */
  initImport(
    request: InitImportFilesRequestModel
  ): Promise<ResponseModel<Array<InitImportVideoItem>>> {
    return new Promise<ResponseModel<Array<InitImportVideoItem>>>(
      async (resolve) => {
        const result = new ResponseModel<Array<InitImportVideoItem>>();

        // Получаем список URL из запроса
        const urls = request.Files.map((file) => file.FileUrl);

        // Сразу запускаем ожидание события инициализации файлов по этим URL
        // Это важно: подписка на событие должна быть ДО запроса импорта, чтобы не пропустить событие
        this.waitInitImportFiles(urls).then((resp) => {
          result.Value = resp;
          return resolve(result);
        });

        // Отправляем запрос на импорт файлов по URL
        // Если он завершится с ошибкой — сохраняем её в ResponseModel и завершаем с ошибкой
        this.api.File.FileService.importFilesFromUrlAsync(
          this.mapInitImportFilesFromUrlRequest(request)
        ).catch((error) => {
          console.error("ERROR initImport", error);
          result.IsSuccess = false;
          result.ErrorCode = error?.ErrorCode;
          result.ErrorMessage = error?.ErrorMessage;
          return resolve(result);
        });
      }
    );
  }
  /**
   * Отмена импорта файла
   * @param request
   */
  abortImport(data: AbortVideoRequestModel): Promise<ResponseModel> {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      const request = new AbortVideoRequestModel({
        VideoId: data.VideoId,
      });

      this.api.File.VideoService.abortVideoAsync(request)
        .then(() => {
          resolve(result);
        })
        .catch((e) => {
          console.error("ERROR abortImport", e);
          result.IsSuccess = false;
          result.ErrorCode = e.ErrorCode;
          result.ErrorMessage = e.ErrorMessage;
          resolve(result);
        });
    });
  }
}
